import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import {
	MapContainer,
	Marker,
	Polyline,
	TileLayer,
	Tooltip,
	useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'react-leaflet-markercluster/styles';
import { useTranslate } from '@tolgee/react';
import {
	TrackingCheckpointSource,
	TrackingOrder,
	TrackingTruck,
	TrackingRouteMode,
} from '../orderTracking.interface';
import {
	TRACKING_MAP_PADDING,
	TRACKING_DRIVER_NOTICES,
	TRACKING_STEPS,
} from '../orderTracking.constants';
import dayjs from 'dayjs';
import {
	driverNotice,
	formatLastSeen,
	isTruckComplete,
	isTruckInTransit,
	showTruckOnMap,
	routeCompleted,
	routeRemaining,
	toTuple,
	truckOnMap,
	truckPosition,
} from '../orderTracking.utils';
import {
	LabelPlacement,
	LABEL_OFFSETS,
	placeLabels,
	samePlacements,
	truckCoreRect,
	LabelledPoint,
	Rect,
	TRUCK_CORE_RADIUS,
	LABEL_GAP,
} from '../trackingLabels.utils';
import TrackingZoomControl from './TrackingZoomControl';
import TruckMotion from './TruckMotion';
import { TruckMotionFrame, TruckMotionTarget } from '../truckMotion.controller';
import CarrierCompanyTruck from '../../../../assets/CarrierCompanyTruck';
import { Checkmark } from '../../../../assets/Checkmark';
import { GeoService } from '../../../../services/geo.services';

interface TrackingMapProps {
	order: TrackingOrder;
	selectedTruckId?: number;
	onSelectTruck: (truckId: number) => void;
	bottomInset?: number;
	mode?: TrackingRouteMode;
}

const APPROACH_CASING = {
	color: '#FFFFFF',
	weight: 10,
	opacity: 0.85,
	lineCap: 'round' as const,
	lineJoin: 'round' as const,
	className: 'tracking-route-casing tracking-route-enter',
};

const APPROACH_LINE = {
	color: '#FFB352',
	weight: 5,
	opacity: 0.95,
	dashArray: '2 10',
	lineCap: 'round' as const,
	lineJoin: 'round' as const,
	className: 'tracking-route-remaining tracking-route-enter',
};

const escapeHtml = (value: string) =>
	value.replace(
		/[&<>"']/g,
		(char) =>
			({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[
				char
			] as string),
	);

// Where the truck has been: its driven history in indigo.
const DRIVEN_COLOR = '#484880';

const CHECKPOINT_COLOR: Record<TrackingCheckpointSource, string> = {
	action: '#484880',
	gps: '#484880',
	site: '#A2A2B5',
};
const CHECKPOINT_SIZE = 30;

// A driver action on the map: the truck marker's shape, smaller, with a
// check inside. Grey when it stands at the site rather than at a fix.
const CheckpointMarker: React.FC<{ source: TrackingCheckpointSource }> = ({
	source,
}) => (
	<div
		style={{
			position: 'absolute',
			left: -CHECKPOINT_SIZE / 2,
			top: -CHECKPOINT_SIZE / 2,
			width: CHECKPOINT_SIZE,
			height: CHECKPOINT_SIZE,
			borderRadius: '50%',
			background: CHECKPOINT_COLOR[source],
			border: '3px solid #fff',
			boxSizing: 'border-box',
			display: 'grid',
			placeItems: 'center',
			boxShadow: '0 2px 8px rgba(31,31,39,.28)',
			cursor: 'pointer',
		}}
	>
		<Checkmark color="#FFFFFF" width={12} height={9} strokeWidth="2.5" />
	</div>
);

// The road ahead from where the marker is drawn: the route was fetched from
// an earlier fix, so it is picked up at the vertex nearest the marker.
const roadAheadFrom = (
	road: [number, number][],
	from: [number, number],
): [number, number][] => {
	let nearest = 0;
	let nearestDistance = Infinity;
	road.forEach((point, index) => {
		const distance = Math.hypot(point[0] - from[0], point[1] - from[1]);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearest = index;
		}
	});
	const rest = road.slice(nearest + 1);
	return [from].concat(rest.length ? rest : [road[road.length - 1]]);
};

const checkpointIcon = (source: TrackingCheckpointSource) =>
	L.divIcon({
		className: '',
		iconSize: [0, 0],
		html: renderToStaticMarkup(<CheckpointMarker source={source} />),
	});

const placeKey = (position: { lat: number; lng: number }): string =>
	`${position.lat.toFixed(5)},${position.lng.toFixed(5)}`;

const coordinatesLabel = (position: { lat: number; lng: number }): string =>
	`${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`;

// The address under each pin that is not already at a known site, looked up
// once per spot. Without a login (the public link) the coordinates stand in.
const useCheckpointPlaces = (
	checkpoints: { position: { lat: number; lng: number }; place?: string }[],
): Record<string, string> => {
	const [places, setPlaces] = useState<Record<string, string>>({});
	const pendingRef = useRef(new Set<string>());

	useEffect(() => {
		const canLookUp = !!localStorage.getItem('access_token');
		checkpoints.forEach((checkpoint) => {
			if (checkpoint.place) return;
			const key = placeKey(checkpoint.position);
			if (places[key] || pendingRef.current.has(key)) return;
			pendingRef.current.add(key);
			const fallback = coordinatesLabel(checkpoint.position);
			if (!canLookUp) {
				setPlaces((prev) => ({ ...prev, [key]: fallback }));
				return;
			}
			GeoService.reverseGeocode({
				lat: String(checkpoint.position.lat),
				lng: String(checkpoint.position.lng),
			})
				.then(({ data }) => {
					const first = data?.items?.[0];
					const parts = [
						first?.address?.street,
						first?.address?.city,
						first?.address?.countryName,
					].filter(Boolean);
					return first?.address?.label || parts.join(', ') || fallback;
				})
				.catch(() => fallback)
				.then((label) => setPlaces((prev) => ({ ...prev, [key]: label })));
		});
	}, [checkpoints, places]);

	return places;
};

const pinIcon = (background: string, label: string) =>
	L.divIcon({
		className: '',
		iconSize: [0, 0],
		html: `<div style="position:absolute;right:-8px;top:-13px;display:flex;flex-direction:row-reverse;align-items:center;gap:8px;white-space:nowrap">
			<span style="width:16px;height:16px;border-radius:50%;border:3px solid #fff;background:${background};box-shadow:0 1px 6px rgba(31,31,39,.25);box-sizing:border-box"></span>
			<span style="padding:4px 9px;border-radius:6px;background:#fff;border:1px solid #E8E8EC;font-size:12.5px;font-weight:600;color:#1F1F27">${escapeHtml(
				label,
			)}</span></div>`,
	});

const labelStyle = (placement: LabelPlacement): React.CSSProperties => {
	const { dx, dy, anchorX, anchorY } = LABEL_OFFSETS[placement];
	return {
		position: 'absolute',
		left: dx,
		top: dy,
		transform: `translate(${-anchorX * 100}%, ${-anchorY * 100}%)`,
	};
};

const TruckMarker: React.FC<{
	truck: TrackingTruck;
	selected: boolean;
	label?: string;
	labelPlacement: LabelPlacement;
}> = ({ truck, selected, label, labelPlacement }) => {
	return (
		<div
			style={{
				position: 'absolute',
				left: 0,
				top: 0,
				width: 0,
				height: 0,
				cursor: 'pointer',
			}}
		>
			<div
				style={{
					position: 'absolute',
					left: -TRUCK_CORE_RADIUS,
					top: -TRUCK_CORE_RADIUS,
					display: 'flex',
					alignItems: 'center',
					gap: LABEL_GAP,
					whiteSpace: 'nowrap',
				}}
			>
				<div className="tracking-truck-marker">
					{isTruckInTransit(truck) ? (
						<>
							<span className="tracking-truck-pulse" />
							<span className="tracking-truck-pulse tracking-truck-pulse--lag" />
						</>
					) : null}
					<div
						className="tracking-truck-core"
						style={{
							position: 'relative',
							width: 44,
							height: 44,
							borderRadius: '50%',
							background: '#484880',
							border: '3px solid #fff',
							boxSizing: 'border-box',
							display: 'grid',
							placeItems: 'center',
							boxShadow: `${
								selected ? '0 0 0 4px rgba(72,72,128,.25),' : ''
							}0 2px 10px rgba(31,31,39,.28)`,
						}}
					>
						<CarrierCompanyTruck color="#FFFFFF" width={22} height={22} />
						<div
							style={{
								position: 'absolute',
								top: -7,
								right: -9,
								minWidth: 20,
								height: 20,
								padding: '0 5px',
								boxSizing: 'border-box',
								borderRadius: 10,
								background: '#fff',
								color: '#484880',
								fontSize: 11,
								fontWeight: 800,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								boxShadow: '0 0 0 1px #E8E8EC',
							}}
						>
							{truck.number}
						</div>
					</div>
				</div>
				{truck.speedKmh > 0 ? (
					<div
						data-truck-chip=""
						style={{
							padding: '3px 8px',
							borderRadius: 6,
							background: '#fff',
							border: '1px solid #E8E8EC',
							color: '#1F1F27',
							fontSize: 12.5,
							fontWeight: 600,
							fontVariantNumeric: 'tabular-nums',
							boxShadow: '0 2px 12px rgba(31,31,39,.08)',
						}}
					>
						{truck.speedKmh} km/h
					</div>
				) : null}
			</div>
			{label ? (
				<div
					data-truck-label=""
					style={{
						...labelStyle(labelPlacement),
						padding: '4px 9px',
						borderRadius: 6,
						background: '#fff',
						border: '1px solid #E8E8EC',
						color: '#1F1F27',
						fontSize: 12.5,
						fontWeight: 600,
						lineHeight: 1.4,
						whiteSpace: 'normal',
						width: 'max-content',
						maxWidth: 220,
					}}
				>
					{label}
				</div>
			) : null}
		</div>
	);
};

const truckIcon = (
	truck: TrackingTruck,
	selected: boolean,
	label?: string,
	labelPlacement: LabelPlacement = 'right',
) =>
	L.divIcon({
		className: '',
		iconSize: [0, 0],
		html: renderToStaticMarkup(
			<TruckMarker
				truck={truck}
				selected={selected}
				label={label}
				labelPlacement={labelPlacement}
			/>,
		),
	});

const TruckCluster: React.FC<{ count: number }> = ({ count }) => (
	<div
		style={{
			position: 'absolute',
			left: -24,
			top: -24,
			width: 48,
			height: 48,
			borderRadius: '50%',
			background: '#484880',
			border: '3px solid #fff',
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 1,
			color: '#fff',
			fontSize: 13,
			fontWeight: 800,
			lineHeight: 1,
			fontVariantNumeric: 'tabular-nums',
			cursor: 'pointer',
			boxShadow: '0 0 0 4px rgba(72,72,128,.18), 0 2px 10px rgba(31,31,39,.28)',
		}}
	>
		<CarrierCompanyTruck color="#FFFFFF" width={16} height={16} />
		{count}
	</div>
);

const truckClusterIcon = (cluster: { getChildCount: () => number }) =>
	L.divIcon({
		className: '',
		iconSize: L.point(0, 0),
		html: renderToStaticMarkup(
			<TruckCluster count={cluster.getChildCount()} />,
		),
	});

// Trucks closer than about one marker on screen join one cluster; a click
// zooms in, and trucks still together at the closest zoom fan out.
const TRUCK_CLUSTER_RADIUS_PX = 44;
// Fans trucks out far enough that their 44px cores no longer overlap.
const TRUCK_SPIDERFY_DISTANCE = 2;

type MarkerRefs = React.MutableRefObject<Map<number, L.Marker>>;

// Lays the signal-lost labels out so none covers another label or a truck.
// Runs after every pan, zoom and spiderfy, measuring the labels as rendered.
const TruckLabelLayout: React.FC<{
	markers: MarkerRefs;
	clusterGroup: React.MutableRefObject<L.Evented | null>;
	layoutKey: string;
	selectedTruckId?: number;
	onPlacements: (next: Record<number, LabelPlacement>) => void;
}> = ({ markers, clusterGroup, layoutKey, selectedTruckId, onPlacements }) => {
	const map = useMap();

	const layout = useCallback(() => {
		// Markers slide into place after a zoom or spiderfy; replacing their
		// icons meanwhile would make them jump, so wait for 'animationend'.
		if (
			(clusterGroup.current as { _inZoomAnimation?: number } | null)
				?._inZoomAnimation
		)
			return;
		const points: LabelledPoint[] = [];
		const obstacles: Rect[] = [];
		markers.current.forEach((marker, id) => {
			// hidden inside a cluster
			if (!map.hasLayer(marker)) return;
			const element = marker.getElement();
			if (!element) return;
			const { x, y } = map.latLngToContainerPoint(marker.getLatLng());
			obstacles.push(truckCoreRect(x, y));
			const chip = element.querySelector<HTMLElement>('[data-truck-chip]');
			if (chip) {
				obstacles.push({
					x: x + TRUCK_CORE_RADIUS + LABEL_GAP,
					y: y - chip.offsetHeight / 2,
					width: chip.offsetWidth,
					height: chip.offsetHeight,
				});
			}
			const label = element.querySelector<HTMLElement>('[data-truck-label]');
			if (label) {
				points.push({
					id,
					x,
					y,
					width: label.offsetWidth,
					height: label.offsetHeight,
					priority: id === selectedTruckId ? 1 : 0,
				});
			}
		});
		const size = map.getSize();
		onPlacements(
			placeLabels(points, obstacles, { width: size.x, height: size.y }),
		);
	}, [map, markers, clusterGroup, selectedTruckId, onPlacements]);

	useEffect(() => {
		let frame = 0;
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(layout);
		};
		const mapEvents = 'zoomend moveend resize';
		const groupEvents = 'spiderfied unspiderfied animationend';
		const group = clusterGroup.current;
		map.on(mapEvents, schedule);
		group?.on(groupEvents, schedule);
		// the cluster group adds its markers on a short timer
		const timer = window.setTimeout(schedule, 120);
		schedule();
		return () => {
			cancelAnimationFrame(frame);
			window.clearTimeout(timer);
			map.off(mapEvents, schedule);
			group?.off(groupEvents, schedule);
		};
	}, [map, clusterGroup, layout, layoutKey]);

	return null;
};

const FIT_MAX_ZOOM = 16;

const FitBounds: React.FC<{
	points: [number, number][];
	fitKey: string;
	selected: boolean;
	bottomInset: number;
}> = ({ points, fitKey, selected, bottomInset }) => {
	const map = useMap();
	const pointsRef = useRef(points);
	pointsRef.current = points;

	useEffect(() => {
		const points = pointsRef.current;
		if (!points.length) return;
		const compact = map.getSize().x < 1024;
		const padding = compact
			? selected
				? TRACKING_MAP_PADDING.compactSelected
				: TRACKING_MAP_PADDING.compact
			: selected
			? TRACKING_MAP_PADDING.selected
			: TRACKING_MAP_PADDING.all;
		map.stop();
		map.fitBounds(points, {
			paddingTopLeft: padding.topLeft,
			paddingBottomRight: [
				padding.bottomRight[0],
				padding.bottomRight[1] + bottomInset,
			],
			maxZoom: FIT_MAX_ZOOM,
			animate: false,
		});
	}, [map, fitKey, selected, bottomInset]);

	return null;
};

const TrackingMap: React.FC<TrackingMapProps> = ({
	order,
	selectedTruckId,
	onSelectTruck,
	bottomInset = 0,
	mode = 'toDelivery',
}) => {
	const { t } = useTranslate();
	const markerRefs = useRef(new Map<number, L.Marker>());
	const clusterGroupRef = useRef<L.Evented | null>(null);
	const [labelPlacements, setLabelPlacements] = useState<
		Record<number, LabelPlacement>
	>({});
	const updatePlacements = useCallback(
		(next: Record<number, LabelPlacement>) =>
			setLabelPlacements((prev) => (samePlacements(prev, next) ? prev : next)),
		[],
	);
	// A marker only gets a new position or icon when something it shows
	// changed: a fresh position array makes the cluster group drop and re-add
	// the marker (collapsing a spiderfied cluster), and a fresh icon rebuilds
	// its DOM, so either turns an ordinary render into a flicker.
	const positionCache = useRef(new Map<number, [number, number]>());
	const iconCache = useRef(new Map<number, { key: string; icon: L.DivIcon }>());
	const stablePosition = (truck: TrackingTruck): [number, number] => {
		const next = toTuple(truckPosition(truck, truck.route ?? order.route));
		const prev = positionCache.current.get(truck.id);
		if (prev && prev[0] === next[0] && prev[1] === next[1]) return prev;
		positionCache.current.set(truck.id, next);
		return next;
	};
	const stableIcon = (
		truck: TrackingTruck,
		selected: boolean,
		label?: string,
		placement?: LabelPlacement,
	): L.DivIcon => {
		const key = [
			selected,
			label ?? '',
			placement ?? 'right',
			truck.number,
			truck.speedKmh,
			isTruckInTransit(truck),
		].join('|');
		const cached = iconCache.current.get(truck.id);
		if (cached && cached.key === key) return cached.icon;
		const icon = truckIcon(truck, selected, label, placement);
		iconCache.current.set(truck.id, { key, icon });
		return icon;
	};
	const showMainRoute = mode !== 'toPickup';
	const showApproach = mode !== 'toDelivery';
	const selectedTruck = order.trucks.find(
		(truck) => truck.id === selectedTruckId,
	);
	const activeRoute = selectedTruck?.route ?? order.route;
	// The straight pickup → delivery fallback would read as the real route.
	const drawMainRoute =
		showMainRoute && (!!selectedTruck?.route || order.routeKnown);
	const route = useMemo(() => activeRoute.map(toTuple), [activeRoute]);

	// Step checkpoints are drawn for the truck being looked at: the selected
	// one, or the only one of the order.
	// Driver action pins ride on every history route that is drawn: all
	// trucks in the overview, only the selected one once there is a selection.
	const multipleTrucks = order.trucks.length > 1;
	// The overview of several trucks shows only their markers and the order
	// route; history and action pins come with the truck you pick.
	const detailTrucks = useMemo(
		() =>
			selectedTruck
				? [selectedTruck]
				: multipleTrucks
				? []
				: order.trucks.filter(showTruckOnMap),
		[selectedTruck, multipleTrucks, order.trucks],
	);
	const checkpoints = useMemo(
		() =>
			detailTrucks.flatMap((truck) =>
				(truck.checkpoints ?? []).map((checkpoint) => {
					const step = TRACKING_STEPS.find(
						(item) => item.key === checkpoint.key,
					);
					const name = step
						? t(step.labelKey, step.defaultLabel)
						: checkpoint.key;
					const action = multipleTrucks ? `#${truck.number} ${name}` : name;
					return {
						...checkpoint,
						truckId: truck.id,
						label: `${action} · ${dayjs(checkpoint.at).format('DD.MM HH:mm')}`,
						action,
						time: dayjs(checkpoint.at).format('DD.MM.YYYY HH:mm'),
					};
				}),
			),
		[detailTrucks, multipleTrucks, t],
	);

	const checkpointPlaces = useCheckpointPlaces(checkpoints);

	// Until the real positions are in, a marker would sit at the driver's
	// stored location (often the fleet base) or on the straight fallback line.
	const mappableTrucks = useMemo(
		() => order.trucks.filter((truck) => truckOnMap(order, truck)),
		[order],
	);
	const visibleTrucks = useMemo(
		() =>
			selectedTruck
				? mappableTrucks.filter((truck) => truck.id === selectedTruck.id)
				: mappableTrucks,
		[mappableTrucks, selectedTruck],
	);

	const fitKey = [
		order.id,
		mode,
		selectedTruck?.id ?? 'all',
		route.length,
		mappableTrucks.map((truck) => truck.id).join(','),
	].join('|');

	// The marker's own label: a lost connection, or no word from the driver
	// yet (the marker then only stands on the route where the trip says).
	const markerLabel = (truck: TrackingTruck) => {
		if (truck.signalLost) {
			return t(
				'orderTracking.signalLost',
				'Connection with the driver app lost. Last seen here at {time}.',
				{ time: formatLastSeen(truck.positionAt ?? truck.lastSeenAt) },
			);
		}
		const notice = driverNotice(order, truck, mode);
		if (!notice?.onMarker) return undefined;
		const { labelKey, defaultLabel } = TRACKING_DRIVER_NOTICES[notice.key];
		return t(labelKey, defaultLabel);
	};
	// Where each marker is heading: its live fix, slid to by TruckMotion.
	const motionTargets = useMemo<TruckMotionTarget[]>(
		() =>
			visibleTrucks.map((truck) => {
				const { lat, lng } = truckPosition(truck, truck.route ?? order.route);
				const at = truck.positionAt ? Date.parse(truck.positionAt) : NaN;
				return {
					id: truck.id,
					lat,
					lng,
					at: Number.isFinite(at) ? at : undefined,
					gps: truck.positionSource === 'gps',
					progress: truck.routeProgress,
				};
			}),
		[visibleTrucks, order.route],
	);

	// Re-laid out when the label text (last-seen time) or a truck moves.
	const labelLayoutKey = visibleTrucks
		.map((truck) => {
			const [lat, lng] = toTuple(
				truckPosition(truck, truck.route ?? order.route),
			);
			return `${truck.id}:${lat.toFixed(4)},${lng.toFixed(4)}:${
				markerLabel(truck) ?? ''
			}`;
		})
		.join('|');

	// The road ahead of each truck from where it is now: to the pickup, or
	// to the delivery once loaded. Both drawn in the same "remaining" style.
	const approachLines = useMemo<[number, number][][]>(
		() =>
			detailTrucks
				.map((truck) =>
					showApproach && truck.approachRoute
						? truck.approachRoute
						: showMainRoute && truck.deliveryRoute
						? truck.deliveryRoute
						: [],
				)
				.filter((line) => line.length > 1)
				.map((line) => line.map(toTuple)),
		[showApproach, showMainRoute, detailTrucks],
	);

	const boundsPoints = useMemo<[number, number][]>(() => {
		const base: [number, number][] = showMainRoute
			? route
			: [toTuple(order.pickup.position)];
		const trucks = (selectedTruck ? visibleTrucks : mappableTrucks).map(
			(truck) => toTuple(truckPosition(truck, truck.route ?? order.route)),
		);
		const approach = approachLines.flat();
		const driven = (selectedTruck?.driven ?? []).map(toTuple);
		if (selectedTruck && showMainRoute) return route.concat(approach, driven);
		return base.concat(trucks, approach);
	}, [
		route,
		selectedTruck,
		visibleTrucks,
		mappableTrucks,
		approachLines,
		showMainRoute,
		order.route,
		order.pickup.position,
	]);

	// Coloured up to the truck's current position on the route, so it moves
	// with every live point (the GPS trail only refreshes once a minute).
	const completedLine = useMemo(
		() =>
			selectedTruck
				? routeCompleted(activeRoute, selectedTruck.routeProgress).map(toTuple)
				: [],
		[selectedTruck, activeRoute],
	);

	// What the selected truck actually drove.
	const drivenLine = useMemo(
		() => (selectedTruck?.driven ?? []).map(toTuple),
		[selectedTruck],
	);

	// Once the truck is routed live to the delivery, that line is the real
	// remaining road; the planned one would only draw a second dashed line.
	const remainingLine = useMemo(
		() =>
			selectedTruck && !selectedTruck.deliveryRoute
				? routeRemaining(activeRoute, selectedTruck.routeProgress).map(toTuple)
				: [],
		[selectedTruck, activeRoute],
	);

	// The lines around the truck follow its marker along the slide, so the
	// route looks drawn as it drives: the driven trail ends under the marker
	// instead of jumping to the new fix, the road ahead starts under it, and
	// the completed / remaining split of the planned route moves with it.
	// React puts every line back on its final shape once the slide is over.
	const completedRef = useRef<L.Polyline>(null);
	const remainingRef = useRef<L.Polyline>(null);
	const lastDrivenRef = useRef<L.Polyline>(null);
	const aheadCasingRef = useRef<L.Polyline>(null);
	const aheadLineRef = useRef<L.Polyline>(null);
	const lineTruck = selectedTruck ?? detailTrucks[0];
	const drawAlongSlide = useCallback(
		(frames: TruckMotionFrame[]) => {
			if (!lineTruck) return;
			const frame = frames.find((item) => item.id === lineTruck.id);
			if (!frame) return;
			const here = toTuple(frame.position);

			// Only a trail that ends at the fix the marker slides to is drawn
			// up to the marker; a trail behind the fix stays as it is.
			const trailEnd = drivenLine[drivenLine.length - 1];
			if (
				trailEnd &&
				lineTruck.id === selectedTruck?.id &&
				trailEnd[0] === frame.target.lat &&
				trailEnd[1] === frame.target.lng
			) {
				lastDrivenRef.current?.setLatLngs(
					drivenLine.slice(0, -1).concat([here]),
				);
			}

			if (approachLines[0]) {
				const ahead = roadAheadFrom(approachLines[0], here);
				aheadCasingRef.current?.setLatLngs(ahead);
				aheadLineRef.current?.setLatLngs(ahead);
			}

			if (frame.progress === undefined || lineTruck.id !== selectedTruck?.id) {
				return;
			}
			completedRef.current?.setLatLngs(
				routeCompleted(activeRoute, frame.progress).map(toTuple),
			);
			if (!selectedTruck.deliveryRoute) {
				remainingRef.current?.setLatLngs(
					routeRemaining(activeRoute, frame.progress).map(toTuple),
				);
			}
		},
		[lineTruck, selectedTruck, activeRoute, drivenLine, approachLines],
	);

	return (
		<div className="order-tracking-map absolute inset-0 bg-track-map">
			<MapContainer
				center={route[0] ?? [52, 20]}
				zoom={8}
				zoomControl={false}
				attributionControl={false}
				scrollWheelZoom={true}
				className="w-full h-full z-0"
			>
				<TileLayer
					url={`https://api.maptiler.com/maps/basic-v2/{z}/{x}/{y}.png?key=${process.env.REACT_APP_TILE_LAYER_KEY}`}
					tileSize={512}
					zoomOffset={-1}
				/>
				<TrackingZoomControl />
				<FitBounds
					points={boundsPoints}
					fitKey={`${fitKey}|${approachLines.length}`}
					selected={!!selectedTruck}
					bottomInset={bottomInset}
				/>

				{approachLines.map((line, index) => (
					<React.Fragment key={`approach-${index}`}>
						<Polyline
							ref={index === 0 ? aheadCasingRef : undefined}
							positions={line}
							interactive={false}
							pathOptions={APPROACH_CASING}
						/>
						<Polyline
							ref={index === 0 ? aheadLineRef : undefined}
							positions={line}
							interactive={false}
							pathOptions={APPROACH_LINE}
						/>
					</React.Fragment>
				))}

				{drawMainRoute ? (
					<>
						<Polyline
							positions={route}
							interactive={false}
							pathOptions={{
								color: '#FFFFFF',
								weight: 10,
								opacity: 0.85,
								lineCap: 'round',
								lineJoin: 'round',
								className: 'tracking-route-casing tracking-route-enter',
							}}
						/>
						{!selectedTruck ? (
							<Polyline
								positions={route}
								interactive={false}
								pathOptions={{
									color: '#484880',
									weight: 5,
									opacity: 0.95,
									lineCap: 'round',
									lineJoin: 'round',
									className: 'tracking-route-enter',
								}}
							/>
						) : null}

						{selectedTruck ? (
							<>
								<Polyline
									positions={route}
									interactive={false}
									pathOptions={{
										color: '#484880',
										weight: 5,
										opacity: 0.2,
										lineCap: 'round',
										lineJoin: 'round',
										className: 'tracking-route-enter',
									}}
								/>
								{!drivenLine.length ? (
									<Polyline
										ref={completedRef}
										positions={completedLine}
										interactive={false}
										pathOptions={{
											color: DRIVEN_COLOR,
											weight: 5,
											opacity: 0.95,
											lineCap: 'round',
											lineJoin: 'round',
											className: 'tracking-route-enter',
										}}
									/>
								) : null}
								<Polyline
									ref={remainingRef}
									positions={remainingLine}
									interactive={false}
									pathOptions={{
										color: '#FFB352',
										weight: 5,
										opacity: 0.95,
										dashArray: '2 10',
										lineCap: 'round',
										lineJoin: 'round',
										className: 'tracking-route-remaining tracking-route-enter',
									}}
								/>
							</>
						) : null}
					</>
				) : null}

				{drivenLine.length ? (
					<Polyline
						ref={lastDrivenRef}
						positions={drivenLine}
						interactive={false}
						pathOptions={{
							color: DRIVEN_COLOR,
							weight: 6,
							opacity: 0.95,
							lineCap: 'round',
							lineJoin: 'round',
							className: 'tracking-route-enter',
						}}
					/>
				) : null}

				<Marker
					position={toTuple(order.pickup.position)}
					icon={pinIcon('#18A890', t('orderTracking.pickUpPin', 'Pick-up'))}
					zIndexOffset={-100}
				/>
				{showMainRoute ? (
					<Marker
						position={toTuple(order.delivery.position)}
						icon={pinIcon(
							'#484880',
							t('orderTracking.deliveryPin', 'Delivery'),
						)}
						zIndexOffset={-100}
					/>
				) : null}

				{checkpoints.map((checkpoint) => (
					<Marker
						key={`checkpoint-${checkpoint.truckId}-${checkpoint.key}-${checkpoint.at}`}
						position={toTuple(checkpoint.position)}
						icon={checkpointIcon(checkpoint.source)}
						alt={checkpoint.label}
						zIndexOffset={checkpoint.source === 'action' ? -20 : -50}
					>
						<Tooltip
							direction="top"
							offset={[0, -CHECKPOINT_SIZE / 2 - 2]}
							opacity={1}
							className="tracking-checkpoint-tip"
						>
							<div className="tracking-checkpoint-tip__action">
								{checkpoint.action}
							</div>
							<div>{checkpoint.time}</div>
							<div>
								{checkpoint.place ??
									checkpointPlaces[placeKey(checkpoint.position)] ??
									coordinatesLabel(checkpoint.position)}
							</div>
						</Tooltip>
					</Marker>
				))}

				<MarkerClusterGroup
					ref={clusterGroupRef}
					maxClusterRadius={TRUCK_CLUSTER_RADIUS_PX}
					showCoverageOnHover={false}
					spiderfyOnMaxZoom
					spiderfyDistanceMultiplier={TRUCK_SPIDERFY_DISTANCE}
					spiderLegPolylineOptions={{
						color: '#484880',
						weight: 2,
						opacity: 0.4,
					}}
					iconCreateFunction={truckClusterIcon}
				>
					{visibleTrucks.map((truck) => (
						<Marker
							key={truck.id}
							ref={(marker) => {
								if (marker) markerRefs.current.set(truck.id, marker);
								else markerRefs.current.delete(truck.id);
							}}
							position={stablePosition(truck)}
							icon={stableIcon(
								truck,
								truck.id === selectedTruckId,
								markerLabel(truck),
								labelPlacements[truck.id],
							)}
							zIndexOffset={
								truck.id === selectedTruckId
									? 1000
									: isTruckComplete(truck)
									? 0
									: 100
							}
							eventHandlers={{ click: () => onSelectTruck(truck.id) }}
						/>
					))}
				</MarkerClusterGroup>
				<TruckMotion
					markers={markerRefs}
					targets={motionTargets}
					onFrames={drawAlongSlide}
				/>
				<TruckLabelLayout
					markers={markerRefs}
					clusterGroup={clusterGroupRef}
					layoutKey={labelLayoutKey}
					selectedTruckId={selectedTruckId}
					onPlacements={updatePlacements}
				/>
			</MapContainer>
		</div>
	);
};

export default TrackingMap;
